import { FC, Fragment } from 'react'
import { css } from '@emotion/react'
import Header from '@ui/molecules/Header'
import Footer from '@ui/molecules/Footer'
import { MyTheme, theme } from '@ui/styles/theme'
import 'modern-css-reset'
import { useTranslation } from 'react-i18next'
import { Languages } from '@/types/languages'

type LayoutType = {
  title?: string
  lang?: Languages
  description?: string
  pageType?: 'recruit' | 'other'
  children?: React.ReactNode
}

export const Layout: FC<LayoutType> = ({
  title,
  lang = 'ja',
  description,
  pageType,
  children
}) => {
  const styles = createStyles(theme, !!title)
  const { t, i18n } = useTranslation()

  if (i18n.language !== lang) {
    i18n.changeLanguage(lang, () => {
      console.log('i18n.changeLanguage')
    })
  }

  return (
    <div css={styles.root}>
      <Header lang={lang} />
      {pageType === 'recruit' ? (
        <div css={styles.layoutContainer}>{children}</div>
      ) : (
        <Fragment>
          <div css={styles.container}>
            {(title || description) && (
              <div css={styles.titleArea}>
                {title && <h1 css={styles.title}>{t(title)}</h1>}
                <div css={styles.subArea}>
                  {description && (
                    <h2 css={styles.description}>{description}</h2>
                  )}
                </div>
              </div>
            )}
          </div>
          <div css={styles.content}>{children}</div>
        </Fragment>
      )}
      <Footer lang={lang} />
    </div>
  )
}

const createStyles = (theme: MyTheme, hasTitle: boolean) => {
  const containerHeight = hasTitle ? '330px' : '250px'
  return {
    root: css`
      display: grid;
      grid-template-rows: auto 1fr auto;
      grid-template-columns: 100%;
      min-height: 100vh;
    `,
    container: css`
      position: relative;
      height: ${containerHeight};
      margin-bottom: calc(${containerHeight} * -0.36);
      background: linear-gradient(
        ${theme.colors.primaryDark} 15%,
        ${theme.colors.primary}
      );
      clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 64%);
      transition: all 0.4s ease;
      z-index: 99;
    `,
    layoutContainer: css`
      position: relative;
      background: ${theme.colors.background.innermost};
    `,
    titleArea: css`
      max-width: 1152px;
      margin: 0 auto;
      padding: 120px 32px 70px;
      box-sizing: border-box;
      position: relative;
      ${theme.mq.isTablet} {
        width: calc(100% - 128px);
      }
    `,
    title: css`
      color: ${theme.colors.white};
      font-size: 24px;
      font-weight: bold;
      line-height: 36px;
      position: absolute;
      ${theme.mq.isTablet} {
        left: 0;
      }
    `,
    subArea: css`
      position: absolute;
      right: 0;
      padding-right: 32px;
      text-align: right;
      display: none;
      ${theme.mq.isTablet} {
        display: block;
      }
    `,
    logo: css`
      padding-bottom: 20px;
    `,
    description: css`
      white-space: pre-wrap;
      color: ${theme.colors.white};
      font-size: 14px;
      font-weight: bold;
      line-height: 28px;
    `,
    content: css`
      padding-bottom: 48px;
      background: ${theme.colors.background.innermost};
    `
  }
}
